import React, { useState, useEffect } from "react";

import Select from "react-select";

import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import LotsAPI from "../../API/lots.api";
import LookupsAPI from "../../API/lookups.api";
import LocationSchema from "../../schemas/lots.schema";
import { generateGoogleMapsUrl } from "../../utils/mapsHelpers";
import { Trans, useTranslation } from "react-i18next";
import { trimValues } from "../../utils/Helpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import BaseModal from "./BaseModal";
import EditableImage from "../../components/Images/EditableImages";
import { ENTITIES, ACTIONS } from "../../utils/LogManagement/LogConstants";
const LotModal = ({
    modalButton,
    modalButtonSetter,
    completionHandler,
    data,
    setData,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [lot, setLot] = useState({
        name: "",
        materialId: "",
        locationUrl: "",
        image: "",
        latitude: "",
        longitude: "",
        description: "",
    });
    const [materialDropdown, setMaterialDropdown] = useState([]);
    const [materialValue, setMaterialValue] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        fetchMaterials();
    }, []);

    useEffect(() => {
        if (data && isVisible) {
            setLot({
                name: data.name,
                materialId: data.material?.id,
                image: data.image,
                latitude: data.latitude,
                longitude: data.longitude,
                description: data.description,
                locationUrl: data.locationUrl || "",
            });

            const defaultMaterial = materialDropdown.filter(
                (material) => material.value === data.material?.id
            );
            setMaterialValue(defaultMaterial);
        } else {
            setLot({
                name: "",
                materialId: "",
                image: "",
                latitude: "",
                longitude: "",
                description: "",
                locationUrl: "",
            });
            setData(null);

            setMaterialValue(null);
        }
    }, [isVisible]);

    useEffect(() => {
        if (modalButton) {
            openModal();
            modalButtonSetter(false);
        }
    }, [modalButton]);

    useEffect(() => {
        validate(
            lot,
            LocationSchema.CreateAndEditLotSchema,
            setIsFormValid,
            setError
        );
    }, [lot]);

    const fetchMaterials = async () => {
        try {
            const result = await LookupsAPI.getMaterials();
            if (result) {
                const mappedResult = result.map((material) => {
                    return { value: material?.id, label: material?.name };
                });
                setMaterialDropdown(mappedResult);
            }
        } catch (responseError) {
            ErrorAlert(t("unhandledException"));
        }
    };

    const openModal = () => {
        setIsVisible(true);
    };

    const closeModal = () => {
        setIsVisible(false);
        setIsSubmitting(false);
        setIsFormSubmitted(false);
        setIsFormValid(false);
        setLot({
            name: "",
            materialId: "",
            image: "",
            latitude: "",
            longitude: "",
            description: "",
            locationUrl: "",
        });
        setMaterialValue(null);
        setData(null);
    };

    const handleInputChange = (e) => {
        setLot({
            ...lot,
            [e.target.name]: e.target.value,
        });
    };

    const handleMaterialChange = (newMaterial) => {
        setMaterialValue(newMaterial);
        setLot({
            ...lot,
            materialId: newMaterial.value,
        });
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const newLot = trimValues(lot);
        if (data) {
            //NOTE: LAT AND LONG NULL WHEN CREATED OR EDITED
            LotsAPI.updateLot({
                id: data.id,
                ...newLot,
                longitude: null,
                latitude: null,
            })
                .then((res) => {
                    if (res?.modified) {
                        SuccessAlert(t("editLotSuccessMessage"));
                        LogCapture.info(
                            `Lot updated with name: ${newLot?.name} & id: ${newLot?.id}`,
                            ENTITIES.lots,
                            ACTIONS.lots.update
                        );
                        completionHandler();
                        closeModal();
                    } else {
                        ErrorAlert(t("editLotFailMessage"));
                        LogCapture.error(
                            `Couldn't update lot with name: ${newLot?.name} & id: ${newLot?.id}`
                        );
                    }
                })
                .catch((err) => {
                    ErrorAlert(t("editLotFailMessage"));
                    LogCapture.error(
                        `Couldn't update lot with name: ${newLot?.name} & id: ${newLot?.id}, error: ${err}`
                    );
                })
                .finally(() => {
                    setIsSubmitting(false);
                    setIsFormSubmitted(false);
                });
        } else {
            //NOTE: LAT AND LONG NULL WHEN CREATED OR EDITED
            LotsAPI.createLot({
                ...newLot,
                longitude: null,
                latitude: null,
            })
                .then((result) => {
                    if (result?.inserted) {
                        SuccessAlert(t("createLotSuccessMessage"));
                        LogCapture.info(
                            `Lot created with name: ${newLot?.name}`,
                            ENTITIES.lots,
                            ACTIONS.lots.create
                        );
                        completionHandler();
                        closeModal();
                    } else {
                        LogCapture.error(
                            `Couldn't create lot with name: ${newLot.name}`
                        );
                    }
                })
                .catch((err) => {
                    ErrorAlert(t("createLotFailMessage"));
                    LogCapture.error(
                        `Couldn't create lot with name: ${newLot?.name}, error: ${err}`
                    );
                })
                .finally(() => {
                    setIsSubmitting(false);
                    setIsFormSubmitted(false);
                });
        }
    };

    const inputValueHandler = (property) => {
        return property ? property : "";
    };

    const fields = [
        {
            name: "name",
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="name">Name</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={inputValueHandler(lot.name)}
                    name="name"
                    type="text"
                    placeholder="Ex. Texas"
                    className="input-element"
                />
            ),
        },
        {
            name: "materialId",
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="material">Material</Trans>
                </label>
            ),
            input: (
                <Select
                    placeholder="Select Material..."
                    options={materialDropdown}
                    value={inputValueHandler(materialValue)}
                    onChange={handleMaterialChange}
                    theme={getReactSelectTheme}
                />
            ),
        },
        {
            name: "description",
            label: (
                <label className="uppercase">
                    <Trans i18nKey="description">Description</Trans>
                </label>
            ),
            input: (
                <textarea
                    onChange={handleInputChange}
                    value={inputValueHandler(lot.description)}
                    name="description"
                    type="text"
                    placeholder="Type your description here..."
                    className="input-element min-h-80"
                ></textarea>
            ),
        },
        {
            name: "locationUrl",
            label: (
                <label className="uppercase">
                    <Trans i18nKey="locationUrl">location URL</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={inputValueHandler(generateGoogleMapsUrl(lot))}
                    name="locationUrl"
                    type="text"
                    placeholder="Enter location URL"
                    className="input-element"
                />
            ),
        },
    ];

    const renderUploadImage = () => {
        return (
            <EditableImage
                state={lot}
                stateSetter={setLot}
                defaultImage={
                    require("../../assets/images/location.png").default
                }
            />
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div
                        key={index}
                        className={`input-group ${
                            error[field.name] && isFormSubmitted
                                ? "invalid-warning-input"
                                : ""
                        }`}
                    >
                        {field.label}
                        {field.input}
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error[field.name]} />
                        )}
                    </div>
                );
            })
        );
    };
    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                title={data ? t("editLot") : t("addNewLot")}
                action={{
                    submitAction: handleSubmit,
                    name: data ? t("edit") : t("add"),
                }}
                isSmall
            >
                <div className="main-customer-form-inputs">
                    <div className="upload-img-box flex">
                        <div className="relative">{renderUploadImage()}</div>
                        <div className="w-full">{renderFields()}</div>
                    </div>
                </div>
            </BaseModal>
        </>
    );
};

export default LotModal;
